<script setup lang="ts">
import type { UnwrapRef } from 'vue';
import type { useVuePdfEmbed } from 'vue-pdf-embed';

import type { ButtonProps } from '#layers/ui/public';
import { createReusableTemplate } from '@vueuse/core';
import { useAppConfig } from '#app/config';

type PdfDocument = Exclude<UnwrapRef<ReturnType<typeof useVuePdfEmbed>['doc']>, null>;

defineProps<{
	source: string;
	doc: PdfDocument;
}>();

const currentPage = defineModel<number>('currentPage', { default: 1 });
const fullscreen = defineModel<boolean>('fullscreen', { default: false });

const appConfig = useAppConfig();

const [DefineAction, ReuseAction] = createReusableTemplate<Partial<ButtonProps>>({ inheritAttrs: false });

function toggleFullscreen() {
	fullscreen.value = !fullscreen.value;
}
</script>

<template>
	<DefineAction v-slot="{ $slots: _, label, ...buttonProps }">
		<DTooltip :text="label">
			<DButton variant="ghost" color="neutral" size="sm" v-bind="buttonProps" />
		</DTooltip>
	</DefineAction>

	<div
		class="z-100 h-(--toolbar-height) w-full items-center justify-between gap-2 border-b bg-default px-3 py-2 text-sm"
		:class="{
			'flex shadow-xs': fullscreen,
			'hidden group-hover:flex': !fullscreen,
		}"
	>
		<div class="flex items-center gap-4">
			<div class="flex items-center gap-2">
				<DInput
					v-model="currentPage"
					type="number"
					size="xs"
					class="min-w-12"
					:ui="{
						base: `
							[appearance:textfield] py-1.5 text-right text-sm
							[&::-webkit-inner-spin-button]:appearance-none
							[&::-webkit-outer-spin-button]:appearance-none
						`,
					}"
					:min="1"
					:max="doc.numPages"
					:aria-label="$t('common.page_number')"
				/>
				<span>/</span>
				<span>{{ doc.numPages }}</span>
			</div>
			<DSeparator orientation="vertical" class="h-6" />
			<div class="flex items-center gap-2">
				<ReuseAction :icon="appConfig.ui.icons.download" :label="$t('common.download')" :href="source" target="_blank" download />
			</div>
		</div>
		<ReuseAction
			:icon="fullscreen ? appConfig.ui.icons.exitFullscreen : appConfig.ui.icons.enterFullscreen"
			:label="fullscreen ? $t('file_preview.pdf.leave_full_screen') : $t('file_preview.pdf.enter_full_screen')"
			@click="toggleFullscreen"
		/>
	</div>
</template>
